import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import PageNavbar from '../components/feature/PageNavbar';
import { PATHS } from '../router/paths';
import Footer from './home/components/Footer';

export default function NotFound() {
  const location = useLocation();
  const { t } = useTranslation();

  const quickLinks = [
    { to: PATHS.home, label: t('nav.home') },
    { to: PATHS.homeSluzby, label: t('nav.services') },
    { to: PATHS.cennik, label: t('nav.pricing') },
    { to: PATHS.kontakt, label: t('nav.contact') },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <PageNavbar />

      <main className="relative overflow-hidden pb-16 pt-36 md:pb-24 md:pt-44">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-[-10%] top-[12%] h-80 w-80 rounded-full bg-brand-green/10 blur-3xl" />
          <div className="absolute right-[-8%] top-[28%] h-[26rem] w-[26rem] rounded-full bg-[#304D81]/12 blur-3xl" />
          <div className="absolute bottom-[8%] left-[24%] h-72 w-72 rounded-full bg-[#F2E6C9]/6 blur-3xl" />
        </div>

        <section className="relative z-10 mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
            className="grid gap-8 lg:grid-cols-[minmax(0,1.08fr)_minmax(320px,0.92fr)]"
          >
            <div className="relative overflow-hidden rounded-[34px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.94)_0%,rgba(18,18,18,0.86)_100%)] p-8 shadow-[0_24px_70px_rgba(0,0,0,0.34)] md:p-10">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(163,230,53,0.11),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(48,77,129,0.12),transparent_32%)]" />

              <div className="relative z-10">
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-brand-green">
                  <i className="ri-error-warning-line mr-2 text-base" />
                  {t('notFound.badge')}
                </span>

                <div className="mt-8 flex items-center gap-4">
                  <span className="text-[4.25rem] font-caprasimo leading-none text-brand-green md:text-[5.5rem]">
                    404
                  </span>
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05] text-white/74">
                    <i className="ri-flight-takeoff-line text-[1.45rem]" />
                  </span>
                </div>

                <h1 className="mt-6 max-w-3xl text-4xl font-caprasimo leading-[1.04] text-white md:text-6xl">
                  {t('notFound.title')}
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-8 text-white/72 md:text-lg">
                  {t('notFound.description')}
                </p>

                <p className="mt-4 max-w-2xl text-sm leading-7 text-white/48 md:text-base">
                  {t('notFound.hint')}
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to={PATHS.home}
                    className="inline-flex items-center justify-between rounded-[18px] border border-brand-green/25 bg-gradient-to-r from-brand-green via-[#b4ec4b] to-[#c8f06d] px-5 py-3.5 text-[0.96rem] font-black text-[#0D0D0D] shadow-[0_14px_30px_rgba(163,230,53,0.2)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_34px_rgba(163,230,53,0.26)]"
                  >
                    {t('notFound.homeCta')}
                  </Link>
                  <Link
                    to={PATHS.kontakt}
                    className="inline-flex items-center justify-between rounded-[18px] border border-white/10 bg-white/[0.05] px-5 py-3.5 text-[0.96rem] font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/[0.08]"
                  >
                    {t('notFound.contactCta')}
                  </Link>
                </div>

                <div className="mt-8">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/38">
                    {t('notFound.quickLinksTitle')}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {quickLinks.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-medium text-white/76 transition-all hover:bg-white/[0.07] hover:text-white"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <motion.aside
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08, ease: 'easeOut' }}
              className="relative overflow-hidden rounded-[34px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.9)_0%,rgba(18,18,18,0.82)_100%)] p-8 shadow-[0_24px_70px_rgba(0,0,0,0.3)] md:p-10"
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_72%_18%,rgba(163,230,53,0.12),transparent_26%),radial-gradient(circle_at_22%_82%,rgba(48,77,129,0.12),transparent_28%)]" />

              <div className="relative z-10 flex h-full flex-col justify-between gap-8">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/38">
                    {t('notFound.pathLabel')}
                  </p>
                  <div className="mt-3 rounded-[22px] border border-white/10 bg-black/18 px-5 py-4 font-mono text-sm text-white/72 md:text-base">
                    {location.pathname}
                  </div>
                </div>

                <div className="relative flex min-h-[220px] items-center justify-center overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.03)_0%,rgba(255,255,255,0.01)_100%)]">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.08),transparent_30%),radial-gradient(circle_at_50%_100%,rgba(163,230,53,0.08),transparent_40%)]" />
                  <div className="absolute left-[10%] top-[18%] h-10 w-24 rounded-full bg-white/8 blur-2xl" />
                  <div className="absolute right-[14%] top-[34%] h-8 w-20 rounded-full bg-white/6 blur-2xl" />
                  <div className="absolute left-[22%] bottom-[18%] h-12 w-28 rounded-full bg-white/7 blur-2xl" />

                  <div className="relative text-center">
                    <div className="text-[5.5rem] font-caprasimo leading-none text-white/10 md:text-[7rem]">
                      404
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="inline-flex h-16 w-16 items-center justify-center rounded-[22px] border border-white/10 bg-white/[0.06] text-brand-green shadow-[0_18px_44px_rgba(0,0,0,0.28)]">
                        <i className="ri-flight-takeoff-line text-[1.7rem]" />
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm leading-7 text-white/52 md:text-base">
                  {t('notFound.aside')}
                </p>
              </div>
            </motion.aside>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
